import { Auth } from 'aws-amplify';

import { buildApiUrl } from './apiConfig';
import { ApiEnvelope, getFetchErrorMessage, unwrapApiData } from './apiResponse';

export interface School {
  school_id: string;
  school_slug: string;
  name: string;
  address?: string;
  contact_info?: {
    phone?: string;
    email?: string;
    website?: string;
  };
  created_at: number;
  updated_at: number;
}

export interface UpdateSchoolRequest {
  name?: string;
  school_slug?: string;
  address?: string;
  contact_info?: {
    phone?: string;
    email?: string;
    website?: string;
  };
}

/**
 * Get the authorization header with the current user's token
 * NOTE: Cognito Authorizer requires ID Token, not Access Token
 */
const getAuthHeader = async (): Promise<Record<string, string>> => {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken(); // Changed from getAccessToken() to getIdToken()
    return {
      Authorization: `${token}`,
      'Content-Type': 'application/json',
    };
  } catch (error) {
    throw new Error('Not authenticated');
  }
};

/**
 * Get school details by school ID
 * @param schoolId The ID of the school to retrieve
 * @returns The school details
 */
export const getSchool = async (schoolId: string): Promise<School> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(buildApiUrl(`schools/${schoolId}`, 'v2'), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(await getFetchErrorMessage(response, 'Failed to get school'));
    }

    const body = (await response.json()) as ApiEnvelope<School>;
    return unwrapApiData(body, 'Failed to get school');
  } catch (error: any) {
    console.error('Error getting school:', error);
    throw error;
  }
};

/**
 * Update school details
 * @param schoolId The ID of the school to update
 * @param updates The updates to apply to the school
 * @returns The updated school details
 */
export const updateSchool = async (
  schoolId: string,
  updates: UpdateSchoolRequest,
): Promise<{ message: string; school: School }> => {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(buildApiUrl(`schools/${schoolId}`, 'v2'), {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(await getFetchErrorMessage(response, 'Failed to update school'));
    }

    const body = (await response.json()) as ApiEnvelope<{ message: string; school: School }>;
    return unwrapApiData(body, 'Failed to update school');
  } catch (error: any) {
    console.error('Error updating school:', error);
    throw error;
  }
};
